'use client'

import { Alert, Box, Button, CircularProgress } from '@mui/material'
import type { FileUIPart } from 'ai'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { useGetConversationQuery } from '~/data/api/client/assistant'
import { toUIMessages } from '~/data/api/client/assistant/enhancedEndpoints'

import ChatSession from './ChatSession'
import type { ComposedMessage } from './contextRefs'

type ConversationThreadProps = {
  conversationId: string
  organizationId: string
  initialPrompt?: ComposedMessage
  initialFiles?: FileUIPart[]
}

/**
 * Loads a stored conversation and opens a chat session on its transcript. A conversation created from the empty
 * state has no transcript yet, so it opens straight away and sends its first message.
 */
export default function ConversationThread({
  conversationId,
  organizationId,
  initialPrompt,
  initialFiles
}: ConversationThreadProps) {
  const t = useTranslations('features.AssistantV2')
  // Fixed at mount: once the first message is sent the transcript lives in the chat session, and reading it back
  // from assistant-api would remount the session mid-reply.
  const [isNew] = useState(initialPrompt !== undefined)
  const { data, isLoading, refetch } = useGetConversationQuery({ conversationId }, { skip: isNew })

  if (isNew) {
    return (
      <ChatSession
        conversationId={conversationId}
        organizationId={organizationId}
        initialMessages={[]}
        initialPrompt={initialPrompt}
        initialFiles={initialFiles}
      />
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} aria-label={t('loading')} />
      </Box>
    )
  }

  // A failed refetch keeps the open session on the transcript it already loaded.
  if (!data) {
    return (
      <Alert
        severity="error"
        sx={{ m: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => void refetch()}>
            {t('retry')}
          </Button>
        }
      >
        {t('conversationError')}
      </Alert>
    )
  }

  return (
    <ChatSession
      conversationId={conversationId}
      organizationId={organizationId}
      initialMessages={toUIMessages(data.messages)}
      initialQuota={data.quota}
      readOnly={data.readOnly}
    />
  )
}
